import {
  alpha,
  Box,
  ButtonBase,
  Checkbox,
  FormControlLabel,
  makeStyles,
  Menu,
  MenuItem,
  Typography,
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import dayjs, { Dayjs } from "dayjs";
import React from "react";

import { BottomSheet } from "@/components/BottomSheet";
import { CustomButton } from "@/components/Button";
import { Chevron } from "@/components/Chevron";
import { DatePickerComponent } from "@/components/YearMonthPicker";
import { layoutTokens as layout, semanticColors as colors, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import theme from "@/theme";

import { BalancePreset, validBalanceRange } from "../utils/balanceSummary";

const useStyles = makeStyles({
  chip: {
    display: "flex",
    gap: layout.space[8],
    border: "1px solid " + colors["border/default"],
    borderRadius: layout.radius.full,
    padding: "8px 12px",
    background: colors["surface/card"],
    minWidth: 0,
    maxWidth: "100%",
    ...textStyles["SP/Button/S"],
    [theme.breakpoints.up("md")]: textStyles["PC/Button/M"],
  },
  propertyChip: { [theme.breakpoints.up("md")]: { maxWidth: 320 } },
  label: { overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" },
  option: {
    width: "100%",
    minHeight: 44,
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: layout.space[12],
    textAlign: "left",
    borderRadius: layout.radius.sm,
    ...textStyles["SP/Body/Large"],
    [theme.breakpoints.up("md")]: textStyles["PC/Body/Small"],
  },
  selected: { background: alpha(colors["action/primary"], 0.08), color: colors["text/link"] },
  menu: { width: 240, padding: 8, borderRadius: layout.radius.lg },
  menuList: { padding: 0 },
  check: { color: colors["text/link"], width: 20, height: 20 },
  properties: {
    display: "flex",
    flexDirection: "column",
    gap: layout.space[4],
    paddingBottom: layout.space[16],
    "& .MuiFormControlLabel-root": { marginLeft: -8, marginRight: 0 },
    "& .MuiFormControlLabel-label": { ...textStyles["SP/Body/Large"], overflowWrap: "anywhere" },
  },
  custom: { display: "flex", flexDirection: "column", gap: layout.space[24], paddingBottom: layout.space[16] },
  error: { ...textStyles["SP/Body/Small"], color: colors["status/error"] },
  footer: { display: "flex", gap: layout.space[12], "& > div": { flex: 1 } },
});

export type PropertyOption = { id: number; label: string };
export const BalancePropertyFilter: React.FC<{
  options: PropertyOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}> = ({ options, selectedIds, onChange }) => {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const chosen = options.filter((option) => selectedIds.includes(option.id));
  const allSelected = options.length > 0 && chosen.length === options.length;
  const label = allSelected
    ? `すべての物件（${options.length}件）`
    : chosen.length === 1
      ? chosen[0].label
      : chosen.length
        ? `${chosen.length}件の物件`
        : "物件を選択";
  return (
    <>
      <ButtonBase
        className={`${classes.chip} ${classes.propertyChip}`}
        onClick={() => setOpen(true)}
        aria-label={`表示物件: ${label}`}
        aria-haspopup="dialog"
      >
        <span className={classes.label}>{label}</span>
        <Chevron direction="down" />
      </ButtonBase>
      <BottomSheet
        open={open}
        title="表示する物件"
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        footer={<CustomButton onClick={() => setOpen(false)}>完了</CustomButton>}
      >
        <Box className={classes.properties}>
          <FormControlLabel
            label="すべての物件"
            control={
              <Checkbox
                color="primary"
                checked={allSelected}
                indeterminate={chosen.length > 0 && !allSelected}
                onChange={() => onChange(allSelected ? [] : options.map((option) => option.id))}
              />
            }
          />
          {options.map((option) => (
            <FormControlLabel
              key={option.id}
              label={option.label}
              control={
                <Checkbox
                  color="primary"
                  checked={selectedIds.includes(option.id)}
                  onChange={() =>
                    onChange(
                      selectedIds.includes(option.id)
                        ? selectedIds.filter((id) => id !== option.id)
                        : [...selectedIds, option.id]
                    )
                  }
                />
              }
            />
          ))}
        </Box>
      </BottomSheet>
    </>
  );
};

const presets: { value: BalancePreset; label: string }[] = [
  { value: "month", label: "今月" },
  { value: "previous", label: "先月" },
  { value: "year", label: "今年" },
  { value: "custom", label: "期間を指定する" },
];
export const BalancePeriodFilter: React.FC<{
  preset: BalancePreset;
  start: Dayjs;
  end: Dayjs;
  onChange: (preset: BalancePreset, start?: Dayjs, end?: Dayjs) => void;
}> = ({ preset, start, end, onChange }) => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const [customOpen, setCustomOpen] = React.useState(false);
  const [draftStart, setDraftStart] = React.useState<Date | null>(start.toDate());
  const [draftEnd, setDraftEnd] = React.useState<Date | null>(end.toDate());
  const label =
    preset === "custom"
      ? start.format("YYYY/MM") + "〜" + end.format("YYYY/MM")
      : presets.find((item) => item.value === preset)?.label;
  const choose = (next: BalancePreset) => {
    setAnchor(null);
    if (next === "custom") {
      setDraftStart(start.toDate());
      setDraftEnd(end.toDate());
      setCustomOpen(true);
    } else onChange(next);
  };
  const options = presets.map((item) =>
    isMobile ? (
      <ButtonBase
        key={item.value}
        className={`${classes.option} ${preset === item.value ? classes.selected : ""}`}
        onClick={() => choose(item.value)}
        aria-pressed={preset === item.value}
      >
        {item.label}
        {preset === item.value && <CheckIcon className={classes.check} />}
      </ButtonBase>
    ) : (
      <MenuItem
        key={item.value}
        className={classes.option}
        selected={preset === item.value}
        onClick={() => choose(item.value)}
      >
        {item.label}
        {preset === item.value && <CheckIcon className={classes.check} />}
      </MenuItem>
    )
  );
  const valid = validBalanceRange(draftStart, draftEnd);
  return (
    <>
      <ButtonBase
        className={classes.chip}
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-label={`表示期間: ${label}`}
        aria-haspopup={isMobile ? "dialog" : "menu"}
      >
        <span className={classes.label}>{label}</span>
        <Chevron direction="down" />
      </ButtonBase>
      {isMobile ? (
        <BottomSheet open={!!anchor} title="表示期間" onOpen={() => undefined} onClose={() => setAnchor(null)}>
          {options}
        </BottomSheet>
      ) : (
        <Menu
          open={!!anchor}
          anchorEl={anchor}
          onClose={() => setAnchor(null)}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{ className: classes.menu }}
          MenuListProps={{ className: classes.menuList }}
        >
          {options}
        </Menu>
      )}
      <BottomSheet
        open={customOpen}
        title="期間を指定する"
        onOpen={() => setCustomOpen(true)}
        onClose={() => setCustomOpen(false)}
        footer={
          <Box className={classes.footer}>
            <CustomButton tone="outline" onClick={() => setCustomOpen(false)}>
              キャンセル
            </CustomButton>
            <CustomButton
              disabled={!valid}
              onClick={() => {
                if (!valid) return;
                onChange("custom", dayjs(draftStart).startOf("month"), dayjs(draftEnd).endOf("month"));
                setCustomOpen(false);
              }}
            >
              適用
            </CustomButton>
          </Box>
        }
      >
        <Box className={classes.custom}>
          <DatePickerComponent label="開始月" value={draftStart} onChange={setDraftStart} />
          <DatePickerComponent label="終了月" value={draftEnd} onChange={setDraftEnd} />
          {!valid && (
            <Typography role="alert" className={classes.error}>
              終了月は開始月以降の有効な月を選択してください。
            </Typography>
          )}
        </Box>
      </BottomSheet>
    </>
  );
};
